/**
 * GitHub URL Scanner Component
 *
 * All scan mechanisms always run (pattern scan, VendorGuard, license, AI-BOM, deps).
 * AI analysis runs server-side when configured — no client toggle required.
 * Depth selector controls scan thoroughness. GitHub PAT is persisted to Keycloak.
 */

import { useRef, useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
// Switch component has dark-on-dark visibility issue; using inline toggle instead
import { AlertCircle, ExternalLink, Github, Loader2, CheckCircle, Link2, ChevronDown, ChevronRight, Shield, Brain, Zap, FolderOpen, Upload } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { apiClient, getStoredToken } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgContext } from '@/contexts/OrgContext';
import { REPORTS_QUERY_KEYS } from '@/hooks/useReports';
import { ACTIVE_SCANS_KEY, type ActiveScan } from '@/hooks/useActiveScans';

interface ScanResponse {
  scanId: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  target: string;
  type: string;
  createdAt: string;
  estimatedDuration?: number;
  resultUrl?: string;
}

/** Resolve the best available auth token from context / stored session / cookie */
function resolveAuthToken(contextToken: string | null | undefined): string | null {
  if (contextToken) return contextToken;
  const stored = getStoredToken();
  if (stored) return stored;
  const match = document.cookie.split(';').find((c) => c.trim().startsWith('token='));
  return match ? match.split('=')[1] : null;
}

/** Normalize a GitHub URL for dedup comparison. */
function normalizeRepo(url: string) {
  return url
    .trim()
    .replace(/\/+$/, '')
    .replace(/\.git$/, '');
}

interface GitHubScannerProps {
  /** Called after a scan is successfully queued — useful for closing a parent modal. */
  onScanQueued?: () => void;
  /** Pre-fill the target URL (e.g. when retrying a failed scan). */
  defaultUrl?: string;
}

const GH_TOKEN_KEY = 'guardrail_gh_token';

function persistToken(t: string | null) {
  if (t) localStorage.setItem(GH_TOKEN_KEY, t);
  else localStorage.removeItem(GH_TOKEN_KEY);
}

export function GitHubScanner({ onScanQueued, defaultUrl = '' }: GitHubScannerProps = {}) {
  const [url, setUrl] = useState(defaultUrl);
  // Separate state for the dropdown — decoupled from the text input so the
  // input is always the canonical scan target. Picking from the dropdown
  // populates the input; typing in the input resets the dropdown.
  const [selectedRepo, setSelectedRepo] = useState('');
  const [githubToken, setGithubTokenState] = useState(
    () => localStorage.getItem(GH_TOKEN_KEY) ?? ''
  );
  const [branch, setBranch] = useState('');
  const [depth, setDepth] = useState<'quick' | 'standard' | 'deep'>('standard');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [enableDynamicAnalysis, setEnableDynamicAnalysis] = useState(false);
  const [enableZdeAnalysis, setEnableZdeAnalysis] = useState(false);
  const [zdeEngines, setZdeEngines] = useState<Record<string, boolean>>({
    pad: true,    // Phantom API Detection
    irda: true,   // Intent-Reality Divergence
    bgm: true,    // Behavioral Genome Mapping
    rfia: false,  // Rules File Integrity Analysis
    inv: false,   // Invariant Vault (SLM + knowledge base)
  });
  const [daeEndpoint, setDaeEndpoint] = useState('');
  const [daeProbes, setDaeProbes] = useState<Record<string, boolean>>({
    boundary: true,
    escalation: true,
    jailbreak: true,
    config: true,
    multistep: false,
    'memory-attack': false,
    'denial-of-wallet': false,
  });

  /** Unified setter — keeps localStorage and state in sync */
  const setGithubToken = (t: string) => {
    setGithubTokenState(t);
    persistToken(t || null);
  };
  const { token } = useAuth();
  const { orgHeader } = useOrgContext();
  const queryClient = useQueryClient();
  // Track repos currently queued/in-flight to prevent duplicate submissions.
  const activeScans = useRef<Set<string>>(new Set());
  // Guard: when disconnect is in progress, suppress server-side token hydration
  // to avoid Keycloak propagation-delay races.
  const disconnecting = useRef(false);

  // Load saved GitHub PAT from /v2/me — runs once per session, hydrates localStorage
  useQuery({
    queryKey: ['me', 'githubToken'],
    queryFn: async () => {
      const authToken = resolveAuthToken(token);
      if (!authToken) return null;
      const data = await apiClient.getMe(authToken);
      const saved = data?.user?.githubToken;
      if (saved && saved !== localStorage.getItem(GH_TOKEN_KEY) && !disconnecting.current) {
        setGithubToken(saved);
      }
      return data;
    },
    // On remount with cached data, still apply the stored value from localStorage (seeded in useState init)
    staleTime: 5 * 60 * 1000,
  });

  // ── GitHub Repo List (fetched directly from GitHub API when token is available) ──
  const { data: githubRepos = [] } = useQuery({
    queryKey: ['github', 'repos', githubToken],
    queryFn: async () => {
      const res = await fetch(
        'https://api.github.com/user/repos?type=all&sort=updated&per_page=100',
        {
          headers: {
            Authorization: `Bearer ${githubToken}`,
            Accept: 'application/vnd.github+json',
          },
        }
      );
      if (!res.ok) return [];
      const data = (await res.json()) as Array<{
        full_name: string;
        html_url: string;
        private: boolean;
        description: string | null;
      }>;
      return data;
    },
    enabled: !!githubToken,
    staleTime: 5 * 60 * 1000,
  });

  const repoOptions = useMemo(
    () => githubRepos.map((r) => ({ label: r.full_name, value: r.html_url, priv: r.private })),
    [githubRepos]
  );

  // ── Derive owner/repo from current URL for branch fetching ──
  const selectedFullName = useMemo(() => {
    const trimmed = url.trim();
    const m = trimmed.match(/github\.com\/([^/]+\/[^/]+)/);
    if (!m) return '';
    return m[1].replace(/\.git$/, '').replace(/\/tree\/.*$/, '');
  }, [url]);

  // ── GitHub Branch List (fetched when repo is selected + token available) ──
  const { data: githubBranches = [], isFetching: isFetchingBranches } = useQuery({
    queryKey: ['github', 'branches', selectedFullName, githubToken],
    queryFn: async () => {
      const res = await fetch(
        `https://api.github.com/repos/${selectedFullName}/branches?per_page=100`,
        {
          headers: {
            Authorization: `Bearer ${githubToken}`,
            Accept: 'application/vnd.github+json',
          },
        }
      );
      if (!res.ok) return [];
      const data = (await res.json()) as Array<{ name: string; protected: boolean }>;
      // Guard against non-array responses (e.g. when a URL fragment strips the /branches
      // path segment and the API returns a repo object instead of a branch list).
      if (!Array.isArray(data)) return [];
      return data;
    },
    enabled: !!githubToken && !!selectedFullName,
    staleTime: 2 * 60 * 1000,
    retry: false,
  });

  const branchOptions = useMemo(
    () => githubBranches.map((b) => ({ label: b.name, value: b.name, isProtected: b.protected })),
    [githubBranches]
  );

  // ── GitHub OAuth Connect ──
  const searchParams =
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const isGitHubCallback = searchParams?.get('github_linked') === '1';
  const isGitHubError = searchParams?.get('github_error') === '1';

  // On error callback: show toast once and clean URL
  useQuery({
    queryKey: ['github', 'oauth-error'],
    queryFn: async () => {
      const reason = searchParams?.get('reason') ?? 'unknown';
      toast({
        title: 'GitHub connect failed',
        description: `OAuth error: ${reason}`,
        variant: 'destructive',
      });
      window.history.replaceState({}, '', window.location.pathname);
      return null;
    },
    enabled: isGitHubError,
    staleTime: Infinity,
    retry: false,
  });

  // On success callback: read the saved token from Keycloak, populate field, clean URL
  const { isLoading: isCallbackLoading } = useQuery({
    queryKey: ['github', 'oauth-callback'],
    queryFn: async () => {
      const authToken = resolveAuthToken(token);
      if (!authToken) return null;
      const data = await apiClient.fetchGitHubToken(authToken);
      if (data?.token) setGithubToken(data.token);
      window.history.replaceState({}, '', window.location.pathname);
      queryClient.invalidateQueries({ queryKey: ['me', 'githubToken'] });
      return data;
    },
    enabled: isGitHubCallback,
    staleTime: Infinity,
    retry: false,
  });

  // Connect GitHub: retrieve stored token if already linked, otherwise initiate OAuth flow
  const connectMutation = useMutation({
    mutationFn: async () => {
      const authToken = resolveAuthToken(token);
      if (!authToken) throw new Error('Not authenticated');
      const data = await apiClient.getGitHubConnect(authToken);
      if (data.isLinked) {
        // Token already persisted in Keycloak — fetch it directly, no OAuth needed
        const tokenData = await apiClient.fetchGitHubToken(authToken);
        if (tokenData?.token) {
          setGithubToken(tokenData.token);
          queryClient.invalidateQueries({ queryKey: ['me', 'githubToken'] });
        }
        return;
      }
      window.location.href = data.url;
    },
    onError: (err) =>
      toast({ title: 'GitHub connect failed', description: err.message, variant: 'destructive' }),
  });

  const scanMutation = useMutation({
    mutationFn: async ({ targetUrl, pat }: { targetUrl: string; pat: string }) => {
      const authToken = resolveAuthToken(token);
      if (!authToken)
        throw new Error('No authentication token found. Please refresh the page and try again.');

      // Persist PAT to Keycloak for next login (fire-and-forget)
      apiClient.saveGitHubToken(pat.trim() || null, authToken).catch(console.warn);

      // Build DAE options if enabled and endpoint is provided
      const dynamicAnalysis = enableDynamicAnalysis && daeEndpoint.trim()
        ? {
            targetEndpoint: daeEndpoint.trim(),
            probes: Object.entries(daeProbes).filter(([, v]) => v).map(([k]) => k),
          }
        : undefined;

      return apiClient.scan(
        targetUrl,
        'plugin',
        depth,
        authToken,
        pat || undefined,
        {
          includeAiBom: true,
          includeDependencies: true,
          includeLicenseAnalysis: true,
          includeVendorGuard: true,
          includeThreatModel: true,
          includeCompliance: true,
        },
        orgHeader,
        dynamicAnalysis,
        undefined,
        branch || undefined,
        enableZdeAnalysis || undefined,
        enableZdeAnalysis ? zdeEngines.rfia || undefined : undefined,
        enableZdeAnalysis ? zdeEngines.inv || undefined : undefined,
        enableZdeAnalysis ? zdeEngines : undefined,
      ) as Promise<ScanResponse>;
    },
    onSuccess: (data, variables) => {
      activeScans.current.delete(normalizeRepo(variables.targetUrl));
      queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'githubToken'] });
      queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEYS.all });

      // Register as an active scan so it appears immediately in report lists
      queryClient.setQueryData<ActiveScan[]>(ACTIVE_SCANS_KEY, (old = []) => {
        if (old.some((s) => s.scanId === data.scanId)) return old;
        return [
          {
            scanId: data.scanId,
            target: data.target,
            branch: branch || undefined,
            targetType: 'github-repo',
            status:
              data.status === 'completed' || data.status === 'failed'
                ? 'queued'
                : (data.status as ActiveScan['status']),
            createdAt: data.createdAt,
            estimatedDuration: data.estimatedDuration,
          },
          ...old,
        ];
      });

      toast({
        title: 'Scan queued',
        description: `Scan ID ${data.scanId} is running in the background. Check Reports for results.`,
      });
      setUrl('');
      setSelectedRepo('');
      onScanQueued?.();
    },
    onError: (_err, variables) => {
      activeScans.current.delete(normalizeRepo(variables.targetUrl));
    },
  });

  const handleScan = () => {
    const trimmedUrl = url.trim();
    if (!trimmedUrl) return;
    const githubUrlRegex = /^https:\/\/github\.com\/[^/]+\/[^/]+/;
    if (!githubUrlRegex.test(trimmedUrl)) {
      toast({
        title: 'Invalid URL',
        description: 'Enter a valid GitHub repository URL (e.g., https://github.com/owner/repo)',
        variant: 'destructive',
      });
      return;
    }
    const key = normalizeRepo(trimmedUrl);
    if (activeScans.current.has(key)) {
      toast({
        title: 'Already scanning',
        description: `${key} is already queued. Check the Reports page for progress.`,
      });
      return;
    }
    activeScans.current.add(key);
    scanMutation.mutate({ targetUrl: trimmedUrl, pat: githubToken });
  };


  // GRTTL4C-125: Local folder scan state
  const [scanMode, setScanMode] = useState<'github' | 'local'>('github');
  const [localFiles, setLocalFiles] = useState<Array<{ path: string; content: string }>>([]);
  const [localFolderName, setLocalFolderName] = useState<string>('');
  const [localFileError, setLocalFileError] = useState<string | null>(null);
  const [localFileProgress, setLocalFileProgress] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracks whether the folder picker is open so we can clear the spinner on cancel
  const pickingRef = useRef(false);

  const BINARY_EXTENSIONS = new Set([
    '.png','.jpg','.jpeg','.gif','.webp','.svg','.ico','.bmp','.tiff',
    '.pdf','.doc','.docx','.xls','.xlsx','.ppt','.pptx',
    '.zip','.tar','.gz','.rar','.7z','.jar','.war',
    '.exe','.dll','.so','.dylib','.wasm','.bin','.o','.a',
    '.ttf','.otf','.woff','.woff2','.eot',
    '.mp3','.mp4','.wav','.ogg','.avi','.mov','.mkv',
  ]);

  const SKIP_DIRS = new Set(['node_modules', '.git', 'dist', 'build', '.next', '.nuxt', 'coverage', '__pycache__']);
  const SKIP_FILES = new Set(['package-lock.json', 'yarn.lock', 'pnpm-lock.yaml', 'bun.lockb', 'Cargo.lock', 'poetry.lock', 'Pipfile.lock', 'composer.lock']);
  const MAX_FILES = 500;
  const MAX_TOTAL_BYTES = 10 * 1024 * 1024; // 10MB

  const handleLocalFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    pickingRef.current = false;
    if (!fileList || fileList.length === 0) {
      setLocalFileProgress(null);
      return;
    }

    // Reset state synchronously so UI is clean before we start
    setLocalFileError(null);
    setLocalFiles([]);
    setLocalFolderName('');
    setLocalFileProgress('Reading files…');

    // Yield for two animation frames so React flushes and the browser paints
    // the spinner before file reading starts. file.text() on in-memory File
    // objects is near-instant, so we also enforce a minimum display time.
    const readStart = Date.now();
    await new Promise<void>((r) => setTimeout(r, 32));

    const collected: Array<{ path: string; content: string }> = [];
    let totalBytes = 0;

    const firstRelative = fileList[0].webkitRelativePath || fileList[0].name;
    const folderName = firstRelative.split('/')[0] || 'local';
    setLocalFolderName(folderName);

    // Build a filtered list of File objects first (sync, fast)
    const eligible: Array<{ file: File; relPath: string }> = [];
    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const relPath = file.webkitRelativePath || file.name;
      const parts = relPath.split('/');
      if (parts.some((p) => SKIP_DIRS.has(p))) continue;
      if (SKIP_FILES.has(parts[parts.length - 1])) continue;
      const ext = '.' + (parts[parts.length - 1].split('.').pop() || '');
      if (BINARY_EXTENSIONS.has(ext.toLowerCase())) continue;
      if (eligible.length >= MAX_FILES) continue;
      eligible.push({ file, relPath });
    }

    // Read in chunks of 50, yielding between chunks so React can re-render
    const CHUNK = 50;
    for (let i = 0; i < eligible.length; i += CHUNK) {
      if (totalBytes >= MAX_TOTAL_BYTES) break;
      const chunk = eligible.slice(i, i + CHUNK);
      const results = await Promise.all(
        chunk.map(async ({ file, relPath }) => {
          try { return { path: relPath, content: await file.text(), size: file.size }; }
          catch { return null; }
        })
      );
      for (const r of results) {
        if (!r || totalBytes + r.size > MAX_TOTAL_BYTES) continue;
        collected.push({ path: r.path, content: r.content });
        totalBytes += r.size;
      }
      setLocalFileProgress(`Reading files… ${collected.length} / ~${eligible.length}`);
      // Yield between chunks so the progress update paints
      await new Promise<void>((r) => setTimeout(r, 0));
    }

    if (collected.length === 0) {
      setLocalFileError('No readable source files found in this folder.');
      setLocalFileProgress(null);
      return;
    }

    // Enforce minimum 300 ms spinner visibility so fast reads don't flash
    const elapsed = Date.now() - readStart;
    // Enforce minimum 300 ms so fast reads don't produce an invisible flash
    if (elapsed < 300) await new Promise<void>((r) => setTimeout(r, 300 - elapsed));
    setLocalFiles(collected);
    setLocalFileProgress(null);
  };

  const handleLocalScan = () => {
    if (localFiles.length === 0) {
      setLocalFileError('Select a folder first.');
      return;
    }
    const authToken = resolveAuthToken(token);
    if (!authToken) {
      toast({ title: 'Not authenticated', description: 'Please refresh the page.', variant: 'destructive' });
      return;
    }
    const target = `local:${localFolderName || 'folder'}`;
    localScanMutation.mutate({ target, files: localFiles, authToken });
  };

  const localScanMutation = useMutation({
    mutationFn: async ({ target, files, authToken }: { target: string; files: Array<{ path: string; content: string }>; authToken: string }) => {
      return apiClient.scan(
        target, 'code', depth, authToken,
        undefined,
        { includeAiBom: true, includeDependencies: true, includeLicenseAnalysis: true, includeVendorGuard: false, includeThreatModel: true, includeCompliance: true },
        orgHeader,
        undefined, undefined, undefined,
        enableZdeAnalysis || undefined,
        enableZdeAnalysis ? zdeEngines.rfia || undefined : undefined,
        enableZdeAnalysis ? zdeEngines.inv || undefined : undefined,
        enableZdeAnalysis ? zdeEngines : undefined,
        files,
      ) as Promise<ScanResponse>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEYS.all });
      queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });

      // Register as an active scan so useActiveScans polls it and invalidates
      // reports/dashboard when it completes — same pattern as the GitHub mutation.
      queryClient.setQueryData<ActiveScan[]>(ACTIVE_SCANS_KEY, (old = []) => {
        if (old.some((s) => s.scanId === data.scanId)) return old;
        return [
          {
            scanId: data.scanId,
            target: data.target,
            targetType: 'code',
            status:
              data.status === 'completed' || data.status === 'failed'
                ? 'queued'
                : (data.status as ActiveScan['status']),
            createdAt: data.createdAt,
            estimatedDuration: data.estimatedDuration,
          },
          ...old,
        ];
      });

      toast({ title: 'Local scan started', description: `Scan ID: ${data.scanId}` });
      if (onScanQueued) onScanQueued();
    },
    onError: (err: Error) => {
      toast({ title: 'Local scan failed', description: err.message, variant: 'destructive' });
    },
  });

  const isLoading = scanMutation.isPending;
  const error = scanMutation.error;

  return (
    <Card className="bg-zinc-800 border-zinc-700">
      <CardHeader className="pb-3">
        <CardTitle className="text-white flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Security Scan
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Scan a GitHub repository or upload a local folder
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* GRTTL4C-125: Scan mode selector */}
        <Tabs value={scanMode} onValueChange={(v) => setScanMode(v as 'github' | 'local')} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-zinc-900 border border-zinc-700">
            <TabsTrigger value="github" className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white text-zinc-400 flex items-center gap-1.5">
              <Github className="w-3.5 h-3.5" />
              GitHub Repository
            </TabsTrigger>
            <TabsTrigger value="local" className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white text-zinc-400 flex items-center gap-1.5">
              <FolderOpen className="w-3.5 h-3.5" />
              Local Folder
            </TabsTrigger>
          </TabsList>

          <TabsContent value="local" className="mt-3 space-y-4">
            {/* Local folder scan UI */}
            <div className="space-y-3">
              <div>
                <p className="text-xs text-zinc-400 font-medium mb-1.5">Select a local folder to scan</p>
                <p className="text-xs text-zinc-500 mb-3">
                  Reads files directly in your browser — nothing is stored locally. Skips <code className="text-zinc-400">node_modules</code>, <code className="text-zinc-400">.git</code>, binaries, and lockfiles.
                  VendorGuard and GitHub-specific checks are not available for local scans.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  webkitdirectory=""
                  multiple
                  className="hidden"
                  onChange={handleLocalFileChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  className={`w-full border-zinc-600 ${localFileProgress ? 'text-white border-zinc-400' : 'text-zinc-300 hover:text-white hover:border-zinc-400'} bg-zinc-900/60`}
                  onClick={() => {
                    if (pickingRef.current) return;
                    pickingRef.current = true;
                    setLocalFileProgress('Selecting folder…');
                    // Clear spinner if user cancels the picker without selecting a file.
                    // The input 'cancel' event fires specifically for this case and is
                    // more reliable than window focus (which fires too eagerly on macOS).
                    const input = fileInputRef.current;
                    if (input) {
                      input.addEventListener('cancel', () => {
                        pickingRef.current = false;
                        setLocalFileProgress(null);
                      }, { once: true });
                    }
                    fileInputRef.current?.click();
                  }}
                  disabled={localScanMutation.isPending}
                >
                  {localFileProgress
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin text-white" />{localFileProgress}</>
                    : <><Upload className="w-4 h-4 mr-2" />{localFolderName ? `📁 ${localFolderName} (${localFiles.length} files)` : 'Choose Folder'}</>
                  }
                </Button>
              </div>

              {localFileProgress && (
                <p className="text-xs text-zinc-400 animate-pulse">{localFileProgress}</p>
              )}

              {localFileError && (
                <div className="flex items-center gap-2 text-red-400 text-xs bg-red-950/40 border border-red-800/50 rounded-md px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {localFileError}
                </div>
              )}

              {localFiles.length > 0 && !localFileError && (
                <div className="text-xs text-zinc-400 bg-zinc-900/60 border border-zinc-700 rounded-md px-3 py-2 space-y-0.5">
                  <p className="text-zinc-300 font-medium">📁 {localFolderName}</p>
                  <p>{localFiles.length} files ready · {(localFiles.reduce((s, f) => s + f.content.length, 0) / 1024).toFixed(0)} KB</p>
                </div>
              )}

              {localScanMutation.isError && (
                <div className="flex items-center gap-2 text-red-400 text-xs bg-red-950/40 border border-red-800/50 rounded-md px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  {localScanMutation.error?.message || 'Scan failed'}
                </div>
              )}

              <Button
                type="button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
                onClick={handleLocalScan}
                disabled={localFiles.length === 0 || localScanMutation.isPending || !!localFileProgress}
              >
                {localScanMutation.isPending
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Scanning…</>
                  : <><Shield className="w-4 h-4 mr-2" />Scan Local Folder</>}
              </Button>
            </div>
          </TabsContent>
        <TabsContent value="github" className="mt-0">

        {/* Scan depth */}
        <div className="space-y-1.5">
          <p className="text-xs text-zinc-400 font-medium">Scan depth</p>
          <div className="grid grid-cols-3 gap-2">
            {(['quick', 'standard', 'deep'] as const).map((d) => (
              <button
                key={d}
                type="button"
                disabled={isLoading}
                onClick={() => setDepth(d)}
                className={[
                  'px-3 py-2 rounded-md text-xs font-medium border transition-colors capitalize',
                  depth === d
                    ? 'bg-emerald-700 border-emerald-500 text-white'
                    : 'bg-zinc-900/60 border-zinc-600 text-zinc-400 hover:text-white hover:border-zinc-400',
                  isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                ].join(' ')}
              >
                {d === 'quick' ? '⚡ Quick' : d === 'standard' ? '🔍 Standard' : '🔬 Deep'}
              </button>
            ))}
          </div>
          <p className="text-xs text-zinc-500">
            {depth === 'quick'
              ? 'Fast pattern scan — ideal for a quick risk check.'
              : depth === 'standard'
                ? 'Balanced scan — checks structure, deps, and secrets.'
                : 'Comprehensive deep dive — all checks including threat model.'}
          </p>
        </div>

        <div className="space-y-2">
          {/* Repo picker — visible when GitHub is connected and repos are loaded */}
          {repoOptions.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-zinc-400 font-medium">Pick a repository</p>
              <select
                className="w-full bg-zinc-900 border border-zinc-600 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 disabled:opacity-50"
                value={selectedRepo}
                onChange={(e) => {
                  setSelectedRepo(e.target.value);
                  setUrl(e.target.value);
                }}
                disabled={isLoading}
              >
                <option value="">Select a repository…</option>
                {repoOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.priv ? '🔒 ' : '📂 '}
                    {r.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-zinc-500">
                Selecting above populates the URL — you can also type or paste any URL directly:
              </p>
            </div>
          )}
          <Input
            type="url"
            placeholder="https://github.com/owner/repository"
            value={url}
            onChange={(e) => {
              const val = e.target.value;
              setUrl(val);
              // Typing overrides any dropdown selection — reset it to avoid confusion
              if (selectedRepo) setSelectedRepo('');
              // Auto-extract branch from /tree/<branch> URLs
              const treePart = val.match(/github\.com\/[^/]+\/[^/]+\/tree\/(.+?)(?:\/)?$/);
              if (treePart) setBranch(treePart[1]);
            }}
            className={[
              'bg-zinc-900 border-zinc-600 text-white placeholder-slate-400',
              // Highlight the input when it diverges from the dropdown (user typed something custom)
              url && selectedRepo === '' ? 'ring-1 ring-green-600/60 border-green-700' : '',
            ].join(' ')}
            disabled={isLoading}
          />

          {/* Branch selector — dropdown when branches are available, text input fallback */}
          {branchOptions.length > 0 ? (
            <div className="space-y-1">
              <select
                className="w-full bg-zinc-900 border border-zinc-600 text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-green-500 disabled:opacity-50"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                disabled={isLoading}
              >
                <option value="">Default branch</option>
                {branchOptions.map((b) => (
                  <option key={b.value} value={b.value}>
                    {b.isProtected ? '🔒 ' : ''}{b.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <Input
              type="text"
              placeholder={isFetchingBranches ? 'Loading branches…' : 'Branch (optional — defaults to repo default branch)'}
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              className="bg-zinc-900 border-zinc-600 text-white placeholder-slate-400"
              disabled={isLoading || isFetchingBranches}
            />
          )}

          {/* GitHub auth — connected badge OR connect button + PAT fallback */}
          {githubToken ? (
            <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-green-900/30 border border-green-700">
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <CheckCircle className="w-4 h-4" />
                <span>GitHub connected</span>
              </div>
              <button
                type="button"
                onClick={async () => {
                  disconnecting.current = true;
                  setGithubToken('');
                  try {
                    // Await the Keycloak clear BEFORE refetching — otherwise the
                    // /v2/me query sees the old token and re-populates state.
                    await apiClient.saveGitHubToken(null, resolveAuthToken(token) ?? '');
                  } catch (err) {
                    console.warn('Failed to clear GitHub token from server:', err);
                  }
                  queryClient.invalidateQueries({ queryKey: ['me', 'githubToken'] });
                  // Release the guard after a short delay to cover any in-flight refetch
                  setTimeout(() => {
                    disconnecting.current = false;
                  }, 2000);
                }}
                className="text-xs text-zinc-400 hover:text-red-400 transition-colors"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <>
              <Input
                type="password"
                placeholder="GitHub PAT (optional)"
                value={githubToken}
                onChange={(e) => setGithubToken(e.target.value)}
                className="bg-zinc-900 border-zinc-600 text-white placeholder-slate-400"
                disabled={isLoading}
              />
              <Button
                type="button"
                onClick={() => connectMutation.mutate()}
                disabled={connectMutation.isPending || isCallbackLoading || isLoading}
                className="w-full bg-zinc-700 hover:bg-zinc-600 border border-zinc-500 text-zinc-200 gap-2"
              >
                {isCallbackLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Finishing GitHub connect…
                  </>
                ) : connectMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Redirecting to GitHub…
                  </>
                ) : (
                  <>
                    <Link2 className="w-4 h-4" />
                    Connect with GitHub
                  </>
                )}
              </Button>
              <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                <a
                  href="https://github.com/settings/tokens/new?scopes=repo&description=Guardrail+Scanner"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-zinc-200"
                >
                  Generate a GitHub Personal Access Token
                </a>
                <span className="text-zinc-500">(classic PAT, repo scope)</span>
              </div>
            </>
          )}
        </div>

        {/* Advanced Options */}
        <div className="border border-zinc-700 rounded-lg overflow-hidden">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            <span>Advanced Options</span>
            {showAdvanced ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
          </button>

          {showAdvanced && (
            <div className="px-3 pb-3 space-y-3 border-t border-zinc-700">
              {/* AI Analysis info */}
              <div className="flex items-center gap-2 pt-3">
                <Brain className="w-4 h-4 text-purple-400 shrink-0" />
                <p className="text-xs text-zinc-500">
                  <span className="text-zinc-300">AI analysis</span> runs automatically — model provenance, cross-system trust, context overflow, and fine-tuning security checks are included in every scan.
                </p>
              </div>

              {/* Zero-Day Engine — master toggle + individual engine controls */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-red-400" />
                    <div>
                      <p className="text-sm text-zinc-200">Zero-Day Engine</p>
                      <p className="text-xs text-zinc-500">Deep per-file analysis with multiple detection engines</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enableZdeAnalysis}
                    onClick={() => setEnableZdeAnalysis(!enableZdeAnalysis)}
                    disabled={isLoading}
                    className={[
                      'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500',
                      enableZdeAnalysis ? 'bg-red-600 border-red-500' : 'bg-zinc-600 border-zinc-500',
                      isLoading ? 'opacity-50 cursor-not-allowed' : '',
                    ].join(' ')}
                  >
                    <span className={[
                      'pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg transition-transform',
                      enableZdeAnalysis ? 'translate-x-5' : 'translate-x-0.5',
                    ].join(' ')} />
                  </button>
                </div>

                {/* Individual ZDE engine toggles — visible when ZDE is enabled */}
                {enableZdeAnalysis && (
                  <div className="ml-6 space-y-1.5">
                    {[
                      { key: 'pad', label: 'PAD — Phantom API Detection', desc: 'Detects hallucinated or non-existent API calls', color: 'red' },
                      { key: 'irda', label: 'IRDA — Intent-Reality Divergence', desc: 'Functions that claim to do one thing but do another', color: 'orange' },
                      { key: 'bgm', label: 'BGM — Behavioral Genome', desc: 'Behavioral drift and anomaly detection via code genome mapping', color: 'amber' },
                      { key: 'rfia', label: 'RFIA — Rules File Integrity', desc: 'Hidden directives, unicode attacks, and prompt injection in AI rules files', color: 'cyan' },
                      { key: 'inv', label: 'INV — Invariant Vault', desc: 'SLM-powered analysis using zero-day exploit invariant knowledge base', color: 'violet' },
                    ].map(({ key, label, desc, color }) => (
                      <label
                        key={key}
                        className="flex items-start gap-2 cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={zdeEngines[key] ?? false}
                          onChange={(e) => setZdeEngines((prev) => ({ ...prev, [key]: e.target.checked }))}
                          className={`mt-0.5 rounded border-zinc-600 bg-zinc-900 text-${color}-500 focus:ring-${color}-500 focus:ring-offset-0`}
                          disabled={isLoading}
                        />
                        <div className="min-w-0">
                          <p className="text-xs text-zinc-300 group-hover:text-white transition-colors">{label}</p>
                          <p className="text-[11px] text-zinc-600">{desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Dynamic Analysis toggle + config */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-amber-400" />
                    <div>
                      <p className="text-sm text-zinc-200">Dynamic Analysis</p>
                      <p className="text-xs text-zinc-500">Live probes against an API endpoint (requires paid plan)</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enableDynamicAnalysis}
                    onClick={() => setEnableDynamicAnalysis(!enableDynamicAnalysis)}
                    disabled={isLoading}
                    className={[
                      'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500',
                      enableDynamicAnalysis ? 'bg-green-600 border-green-500' : 'bg-zinc-600 border-zinc-500',
                      isLoading ? 'opacity-50 cursor-not-allowed' : '',
                    ].join(' ')}
                  >
                    <span className={[
                      'pointer-events-none block h-4 w-4 rounded-full bg-white shadow-lg transition-transform',
                      enableDynamicAnalysis ? 'translate-x-5' : 'translate-x-0.5',
                    ].join(' ')} />
                  </button>
                </div>

                {enableDynamicAnalysis && (
                  <div className="ml-6 space-y-2">
                    <Input
                      type="url"
                      placeholder="https://api.example.com/chat"
                      value={daeEndpoint}
                      onChange={(e) => setDaeEndpoint(e.target.value)}
                      className="bg-zinc-900 border-zinc-600 text-white placeholder-slate-400 text-sm"
                      disabled={isLoading}
                    />
                    <p className="text-xs text-zinc-500">Target endpoint to probe (must be an endpoint you own or have authorization to test)</p>

                    <div className="grid grid-cols-2 gap-1.5">
                      {Object.entries(daeProbes).map(([probe, enabled]) => (
                        <label
                          key={probe}
                          className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer hover:text-zinc-200"
                        >
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => setDaeProbes((prev) => ({ ...prev, [probe]: e.target.checked }))}
                            className="rounded border-zinc-600 bg-zinc-900 text-green-500 focus:ring-green-500 focus:ring-offset-0"
                            disabled={isLoading}
                          />
                          {probe.replace(/-/g, ' ')}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <Button
          onClick={handleScan}
          disabled={isLoading || !url.trim()}
          className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Scanning...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Scan
            </>
          )}
        </Button>

        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error.message || 'Failed to start scan. Please try again.'}
          </div>
        )}

        <div className="text-xs text-zinc-500">
          {`${depth} scan · all mechanisms + AI analysis${enableZdeAnalysis ? ` + ZDE (${Object.entries(zdeEngines).filter(([, v]) => v).map(([k]) => k.toUpperCase()).join(', ') || 'none'})` : ''}${enableDynamicAnalysis ? ' + dynamic probes' : ''} · PAT saved to account.`}
        </div>

          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
